"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var useCancellablePromise_1 = __importDefault(require("@sprinx/react-hooks/useCancellablePromise"));
var react_1 = __importDefault(require("react"));
var tiny_invariant_1 = __importDefault(require("tiny-invariant"));
var formErrorsToIsValid_1 = __importDefault(require("../formErrorsToIsValid"));
var getTouchedForValues_1 = __importDefault(require("../getTouchedForValues"));
var useFastestValidator_1 = __importDefault(require("../useFastestValidator"));
function FormikExtendedHandlers(_a) {
    var children = _a.children, formState = _a.formState, setFormState = _a.setFormState, resetForm = _a.resetForm, onBeforeChange = _a.onBeforeChange, onFormReleased = _a.onFormReleased, onSave = _a.onSave, pOnSubmit = _a.onSubmit, translator = _a.translator, pValidate = _a.validate, _b = _a.validateOnBlur, validateOnBlur = _b === void 0 ? true : _b, _c = _a.validateOnChange, validateOnChange = _c === void 0 ? true : _c, _d = _a.validationFastest, validationFastest = _d === void 0 ? false : _d, validationSchema = _a.validationSchema, ext = __rest(_a, ["children", "formState", "setFormState", "resetForm", "onBeforeChange", "onFormReleased", "onSave", "onSubmit", "translator", "validate", "validateOnBlur", "validateOnChange", "validationFastest", "validationSchema"]);
    tiny_invariant_1.default(onSave || pOnSubmit, 'FormikExtended / FormikExtendedRecoil requires at least one of `onSubmit` or `onSave` props to be defined.');
    tiny_invariant_1.default(pValidate || validationSchema, 'FormikExtended / FormikExtendedRecoil requires at least one of `validate` or `validationSchema` props to be defined.');
    var formSubmittingLockRef = react_1.default.useRef(false);
    // ////////////////
    //   Parent Adaptation
    // ////////////////
    var cancellable = useCancellablePromise_1.default();
    var validator = useFastestValidator_1.default(translator, pValidate, validationSchema, typeof validationFastest === 'boolean' ? {} : validationFastest);
    pValidate || (function () { return ({}); });
    var handleFormSubmit = react_1.default.useCallback(function (values, submitProps) {
        if (pOnSubmit) {
            return cancellable(pOnSubmit(values, submitProps));
        }
        tiny_invariant_1.default(onSave);
        return cancellable(onSave(values))
            .then(function (saved) {
            submitProps.setSubmitting(false);
            return saved;
        })
            .then(function (saved) { return (onFormReleased ? onFormReleased(saved) : saved); })
            .catch(function (err) {
            submitProps.setSubmitting(false);
            throw err;
        });
    }, [cancellable, onFormReleased, onSave, pOnSubmit]);
    var handleBeforeChange = react_1.default.useCallback(function (prevValues, currentValues, currentTouched) {
        if (!onBeforeChange)
            return [currentValues, currentTouched];
        var onBeforeChangeRes = onBeforeChange(prevValues, currentValues, currentTouched);
        return [onBeforeChangeRes.values, onBeforeChangeRes.touched || currentTouched];
    }, [onBeforeChange]);
    // ////////////////
    //   Setters
    // ////////////////
    var setErrors = react_1.default.useCallback(function (fields) {
        setFormState(function (prev) { return (__assign(__assign({}, prev), { errors: fields })); });
    }, [setFormState]);
    var setFieldError = react_1.default.useCallback(function (field, errorMsg) {
        setFormState(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), { errors: __assign(__assign({}, prev.errors), (_a = {}, _a[field] = errorMsg, _a)) }));
        });
    }, [setFormState]);
    var setFieldTouched = react_1.default.useCallback(function (field, touched, shouldValidate) {
        setFormState(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), { isValidating: !!shouldValidate, touched: __assign(__assign({}, prev.touched), (_a = {}, _a[field] = touched, _a)) }));
        });
    }, [setFormState]);
    var setFieldValue = react_1.default.useCallback(function (field, value, shouldValidate) {
        setFormState(function (prev) {
            var _a;
            var _b = __read(handleBeforeChange(prev.values, __assign(__assign({}, prev.values), (_a = {}, _a[field] = value, _a)), prev.touched), 2), nextValues = _b[0], nextTouched = _b[1];
            return __assign(__assign({}, prev), { isValidating: !!shouldValidate, touched: nextTouched, values: nextValues });
        });
    }, [handleBeforeChange, setFormState]);
    var setFieldValueTouched = react_1.default.useCallback(function (field, value, touched, shouldValidate) {
        setFormState(function (prev) {
            var _a, _b;
            var _c = __read(handleBeforeChange(prev.values, __assign(__assign({}, prev.values), (_a = {}, _a[field] = value, _a)), __assign(__assign({}, prev.touched), (_b = {}, _b[field] = touched, _b))), 2), nextValues = _c[0], nextTouched = _c[1];
            return __assign(__assign({}, prev), { isValidating: !!shouldValidate, touched: nextTouched, values: nextValues });
        });
    }, [handleBeforeChange, setFormState]);
    var setStatus = react_1.default.useCallback(function (status) {
        setFormState(function (prev) { return (__assign(__assign({}, prev), { status: status })); });
    }, [setFormState]);
    var setSubmitting = react_1.default.useCallback(function (isSubmitting) {
        setFormState(function (prev) { return (__assign(__assign({}, prev), { isSubmitting: isSubmitting })); });
    }, [setFormState]);
    var setTouched = react_1.default.useCallback(function (touched, shouldValidate) {
        setFormState(function (prev) { return (__assign(__assign({}, prev), { isValidating: !!shouldValidate, touched: touched })); });
    }, [setFormState]);
    var setValues = react_1.default.useCallback(function (values, shouldValidate) {
        setFormState(function (prev) {
            var _a = __read(handleBeforeChange(prev.values, values, prev.touched), 2), nextValues = _a[0], nextTouched = _a[1];
            return __assign(__assign({}, prev), { isValidating: !!shouldValidate, touched: nextTouched, values: nextValues });
        });
    }, [handleBeforeChange, setFormState]);
    var setValuesTouched = react_1.default.useCallback(function (values, touched, shouldValidate) {
        setFormState(function (prev) {
            var _a = __read(handleBeforeChange(prev.values, values, touched), 2), nextValues = _a[0], nextTouched = _a[1];
            return __assign(__assign({}, prev), { isValidating: !!shouldValidate, touched: nextTouched, values: nextValues });
        });
    }, [handleBeforeChange, setFormState]);
    var submitForm = react_1.default.useCallback(function (valuesPartial) {
        formSubmittingLockRef.current = true;
        setFormState(function (prev) {
            var currenctValues = __assign(__assign({}, prev.values), valuesPartial);
            var _a = __read(handleBeforeChange(prev.values, currenctValues, getTouchedForValues_1.default(currenctValues)), 2), nextValues = _a[0], nextTouched = _a[1];
            return __assign(__assign({}, prev), { isSubmitting: true, isValidating: true, touched: nextTouched, values: nextValues });
        });
    }, [handleBeforeChange, setFormState]);
    // // ////////////////
    // //   Submitting
    // // ////////////////
    react_1.default.useEffect(function () {
        // `formSubmittingLockRef` locks fireing of save handler twice.
        if (formState.isSubmitting && !formState.isValidating && formState.isValid && formSubmittingLockRef.current) {
            formSubmittingLockRef.current = false;
            handleFormSubmit(formState.values, {
                resetForm: resetForm,
                setErrors: setErrors,
                setFieldError: setFieldError,
                setFieldTouched: setFieldTouched,
                setFieldValue: setFieldValue,
                setFieldValueTouched: setFieldValueTouched,
                setStatus: setStatus,
                setSubmitting: setSubmitting,
                setTouched: setTouched,
                setValues: setValues,
                setValuesTouched: setValuesTouched,
            }).finally(function () {
                formSubmittingLockRef.current = false;
            });
        }
    }, [
        formState.isSubmitting,
        formState.isValidating,
        formState.isValid,
        formState.values,
        handleFormSubmit,
        resetForm,
        setErrors,
        setFieldError,
        setFieldTouched,
        setFieldValue,
        setFieldValueTouched,
        setStatus,
        setSubmitting,
        setTouched,
        setValues,
        setValuesTouched,
    ]);
    // // ////////////////
    // //   Validation
    // // ////////////////
    react_1.default.useEffect(function () {
        if (formState.isValidating) {
            cancellable(validator(formState.values))
                .then(function (errors) {
                var iv = formErrorsToIsValid_1.default(errors);
                if (!iv && formSubmittingLockRef.current) {
                    formSubmittingLockRef.current = false;
                }
                setFormState(function (prev) { return (__assign(__assign({}, prev), { errors: errors, isSubmitting: !iv ? false : prev.isSubmitting, isValid: iv, isValidating: false })); });
            })
                .catch(function (validatorRunError) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('FormikExtended: Validation process throws an error: ', validatorRunError);
                }
                throw validatorRunError;
            });
        }
    }, [cancellable, formState.isValidating, formState.values, pValidate, setFormState, validator]);
    // ////////////////
    //   Handlers
    // ////////////////
    var handleBlur = react_1.default.useCallback(function (field) { return function (nextValue) {
        var isEvent = function (v) {
            return v.preventDefault && typeof v.preventDefault === 'function' ? true : false;
        };
        setFieldTouched(field, (nextValue === undefined || nextValue === null || isEvent(nextValue)
            ? true
            : nextValue), validateOnBlur);
    }; }, [setFieldTouched, validateOnBlur]);
    var handleChange = react_1.default.useCallback(function (field) { return function (value) {
        setFieldValue(field, value, validateOnChange);
    }; }, [setFieldValue, validateOnChange]);
    var handleReset = react_1.default.useCallback(function (event) {
        if (event) {
            event.preventDefault();
        }
        resetForm();
    }, [resetForm]);
    var handleSubmit = react_1.default.useCallback(function (event, valuesPartial) {
        if (event) {
            event.preventDefault();
        }
        submitForm(valuesPartial);
    }, [submitForm]);
    return children(__assign(__assign({}, ext), { dirty: formState.dirty, errors: formState.errors, isSubmitting: formState.isSubmitting, isValid: formState.isValid, isValidating: formState.isValidating, onBlur: handleBlur, onChange: handleChange, onReset: handleReset, onSubmit: handleSubmit, resetForm: resetForm,
        setErrors: setErrors,
        setFieldError: setFieldError,
        setFieldTouched: setFieldTouched,
        setFieldValue: setFieldValue,
        setFieldValueTouched: setFieldValueTouched,
        setStatus: setStatus,
        setSubmitting: setSubmitting,
        setTouched: setTouched,
        setValues: setValues,
        setValuesTouched: setValuesTouched, status: formState.status, submitForm: submitForm, touched: formState.touched, values: formState.values }));
}
exports.default = FormikExtendedHandlers;
FormikExtendedHandlers.displayName = 'FormikExtendedHandlers';
//# sourceMappingURL=FormikExtendedHandlers.js.map